import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import { useSelector } from 'react-redux';
import { Button, Tooltip } from '@material-ui/core';
import Skeleton from 'react-loading-skeleton';
import { Info } from '@material-ui/icons';
import {
  IconButton,
  TextField,
  Grid,
  Box,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Typography,
  Chip,
  Card,
  CardHeader,
  CardContent
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ChipInput from 'material-ui-chip-input';
import { Design, Details } from 'views/SurveyTemplate/components';
import useTemplate from 'views/SurveyTemplate/useTemplate';
import { SelectInput } from 'components';

const useStyles = makeStyles(theme => ({
  root: {},
  cardHeader: {},
  chipGrid: {
    maxWidth: '80%'
  },
  chipError: {
    backgroundColor: '#e53935',
    color: 'white',
    maxWidth: '80%'
  },
  dialogSubTitle: {
    fontSize: '14px',
    fontWeight: '400',
    color: '#546e7a',
    letterSpacing: '-0.05px'
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500]
  },
  nextButton: {
    marginRight: '16px',
    marginBottom: '8px'
  },
  inputGrid: {
    maxWidth: '40rem'
  },
  selectContainer: {
    display: 'inline',
    minWidth: '14rem',
    margin: theme.spacing(1),
    marginLeft: '0px'
  },
  buttonContainer: {
    display: 'inline',
    margin: theme.spacing(1)
  },
  backButton: {
    marginLeft: '16px',
    marginBottom: '8px'
  },
  sendButton: {
    marginRight: '16px',
    marginBottom: '8px'
  }
}));

const SurveyResultResponseModal = props => {
  const {
    className,
    survey,
    showSurveyResponseModal,
    closeSendResponseModal,
    showTemplateCreateModal,
    openCreateTemplateDialog,
    closeCreateTemplateDialog,
    ...rest
  } = props;

  const {
    surveySettings,
    handleSettingsChange,
    updateTemplateModelProperty,
    shouldShowError,
    getErrorMessage,
    onFocus,
    onBlur
  } = survey;

  const templateList = useSelector(store =>
    store.surveyTemplate ? store.surveyTemplate.list : []
  );
  const isLoading = useSelector(store => store.surveyTemplate.getting);

  const classes = useStyles();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCustomizeTemplate, setIsCustomizeTemplate] = useState(false);

  const template = useTemplate();
  const editor = React.createRef();

  useEffect(() => {
    setIsModalOpen(showSurveyResponseModal || false);
  }, [showSurveyResponseModal]);

  const onClickCustomizeAndSendResponse = event => {
    if (survey.onClickCustomizeAndSend()) {
      setIsCustomizeTemplate(true);
      openCreateTemplateDialog(surveySettings.surveyTemplateUuid);
      template.goNext(event);
    }
  };

  const handleAddChip = (e, name) => {
    let current, previous;
    if (name === 'ccAddresses') {
      previous = surveySettings?.ccAddresses;
    } else if (name === 'bccAddresses') {
      previous = surveySettings?.bccAddresses;
    }
    current = previous?.length ? [...previous] : [];
    e.target.value && current.push(e.target.value);
    handleSettingsChange({ target: { name, value: current } });
  };

  const handleDeleteChip = (index, name) => {
    let current, previous;
    if (name === 'ccAddresses') {
      previous = surveySettings?.ccAddresses;
    } else if (name === 'bccAddresses') {
      previous = surveySettings?.bccAddresses;
    }
    current = [...previous];
    current.splice(index, 1);
    handleSettingsChange({ target: { name, value: current } });
  };

  const getValidTemplates = () => {
    return {
      list: templateList.filter(
        temp => {
          const matchesSchema = temp.schemaIdentifier === survey.thisSurvey.schemaIdentifier && temp.active;
          if (survey.thisSurvey.subscriberListId) {
            return matchesSchema && temp.subscriberListId === survey.thisSurvey.subscriberListId;
          }
          return matchesSchema;
        }
      ),
      keyId: 'uuid',
      valueId: 'uuid',
      displayValueId: 'name'
    };
  };

  return (
    <Dialog
      open={isModalOpen}
      aria-labelledby="form-dialog-title"
      fullWidth
      maxWidth="lg">
      <Typography variant="h1">
        <DialogTitle
          style={{ marginTop: '8px' }}
          id="form-dialog-title"
          disableTypography={true}>
          <div>Survey Result Response Settings</div>
          <div className={classes.dialogSubTitle}>
            * indicates a required field
          </div>
        </DialogTitle>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={closeSendResponseModal}>
          <CloseIcon />
        </IconButton>
      </Typography>
      <DialogContent>
        <Card {...rest} className={clsx(classes.root, className)}>
          <CardContent>
            <Grid container spacing={0}>
              {
                <>
                  <Grid item xs={12}>
                    <Grid item md={6} xs={12} className={classes.inputGrid}>
                      <TextField
                        fullWidth
                        label="From Name"
                        margin="dense"
                        name="fromName"
                        onChange={handleSettingsChange}
                        onFocus={onFocus}
                        onBlur={onBlur}
                        required
                        value={surveySettings.fromName || ''}
                        variant="outlined"
                        error={shouldShowError('fromName')}
                        helperText={getErrorMessage('fromName')}
                      />
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <Grid item md={6} xs={12} className={classes.inputGrid}>
                      <TextField
                        fullWidth
                        label="From Address"
                        margin="dense"
                        name="fromAddress"
                        onChange={handleSettingsChange}
                        onFocus={onFocus}
                        onBlur={onBlur}
                        required
                        value={surveySettings.fromAddress || ''}
                        variant="outlined"
                        error={shouldShowError('fromAddress')}
                        helperText={getErrorMessage('fromAddress')}
                      />
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <Grid item md={6} xs={12} className={classes.inputGrid}>
                      <TextField
                        fullWidth
                        label="Reply Address"
                        margin="dense"
                        name="replyAddress"
                        onChange={handleSettingsChange}
                        onFocus={onFocus}
                        onBlur={onBlur}
                        value={surveySettings.replyAddress || ''}
                        required
                        variant="outlined"
                        error={shouldShowError('replyAddress')}
                        helperText={getErrorMessage('replyAddress')}
                      />
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <Grid item md={6} xs={12} className={classes.inputGrid}>
                      <ChipInput
                        fullWidth
                        label="Cc"
                        margin="dense"
                        name="ccAddresses"
                        value={surveySettings.ccAddresses || []}
                        blurBehavior="add"
                        onBlur={chip => handleAddChip(chip, 'ccAddresses')}
                        onDelete={(chip, index) =>
                          handleDeleteChip(index, 'ccAddresses')
                        }
                        variant="outlined"
                        chipRenderer={(
                          { value, isFocused, handleDelete },
                          key
                        ) => (
                          <Chip
                            key={key}
                            onDelete={(chip, index) =>
                              handleDelete(chip, index)
                            }
                            label={value}
                            variant="outlined"
                            style={{ margin: '0 8px 8px 0' }}
                            className={
                              !isFocused &&
                              shouldShowError(`ccAddresses.${key}`)
                                ? classes.chipError
                                : classes.chipGrid
                            }></Chip>
                        )}
                      />
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <Grid item md={6} xs={12} className={classes.inputGrid}>
                      <ChipInput
                        fullWidth
                        label="Bcc"
                        margin="dense"
                        name="bccAddresses"
                        value={surveySettings.bccAddresses || []}
                        blurBehavior="add"
                        onBlur={chip => handleAddChip(chip, 'bccAddresses')}
                        onDelete={(chip, index) =>
                          handleDeleteChip(index, 'bccAddresses')
                        }
                        variant="outlined"
                        chipRenderer={(
                          { value, isFocused, handleDelete },
                          key
                        ) => (
                          <Chip
                            key={key}
                            onDelete={(chip, index) =>
                              handleDelete(chip, index)
                            }
                            label={value}
                            variant="outlined"
                            style={{ margin: '0 8px 8px 0' }}
                            className={
                              !isFocused &&
                              shouldShowError(`bccAddresses.${key}`)
                                ? classes.chipError
                                : classes.chipGrid
                            }></Chip>
                        )}
                      />
                    </Grid>
                  </Grid>
                </>
              }
            </Grid>
          </CardContent>
        </Card>
        <Card style={{ marginTop: '16px' }}>
          <CardHeader
            subheader=""
            title="Template Selection"
            titleTypographyProps={{ variant: 'h3' }}
          />
          <Divider />
          <CardContent>
            <Button
              className={classes.nextButton}
              onClick={() => {
                openCreateTemplateDialog();
              }}
              color="primary"
              variant="contained">
              Create new Template
            </Button>
            <Grid container>
              <Grid item className={classes.selectContainer}>
                {template.isLoading && <Skeleton style={{ height: '40px' }} />}
                {!template.isLoading && (
                  <SelectInput
                    fullWidth
                    label="Select Template"
                    margin="dense"
                    name="surveyTemplateUuid"
                    onChange={e =>
                      updateTemplateModelProperty(
                        templateList.find(temp => temp.uuid === e.target.value) ||
                          {}
                      )
                    }
                    onFocus={onFocus}
                    onBlur={onBlur}
                    required
                    value={surveySettings.surveyTemplateUuid || ''}
                    error={shouldShowError(`surveyTemplateUuid`)}
                    helperText={getErrorMessage(`surveyTemplateUuid`)}
                    options={getValidTemplates()}
                  />
                )}
              </Grid>
              <Grid item className={classes.buttonContainer}>
                <Tooltip
                  title={
                    "To customize the selected template before sending response, click on 'Customize Template & Send Response' button."
                  }>
                  <Info size="small" style={{ marginTop: '14px' }} />
                </Tooltip>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </DialogContent>
      <DialogActions
        className={classes.nextButton}
        style={{ marginBottom: '8px' }}>
        <Button
          onClick={event => onClickCustomizeAndSendResponse(event)}
          disabled={isLoading}
          color="primary"
          variant="contained">
          Customize template & Send Response
        </Button>
        <Button
          onClick={() => survey.onClickSendResponse()}
          disabled={isLoading}
          color="primary"
          variant="contained">
          Send Response
        </Button>
      </DialogActions>

      {!template.isInTemplateDesignDialog && !isCustomizeTemplate ? (
        <Dialog
          open={showTemplateCreateModal}
          aria-labelledby="form-dialog-title"
          fullWidth
          maxWidth="lg">
          <Typography variant="h3">
            <DialogTitle
              style={{ marginTop: '8px' }}
              id="form-dialog-title"
              disableTypography={true}>
              <div>Survey Result Response Template Details</div>
              <div className={classes.dialogSubTitle}>
                * indicates a required field
              </div>
            </DialogTitle>
            <IconButton
              aria-label="close"
              className={classes.closeButton}
              onClick={() => {
                template.resetValidationErrors();
                closeCreateTemplateDialog();
              }}>
              <CloseIcon />
            </IconButton>
          </Typography>
          <DialogContent>
            {template.isLoading && (
              <Box style={{ width: '50%' }}>
                {' '}
                <CircularProgress style={{ float: 'right' }} />
              </Box>
            )}
            {!template.isLoading && (
              <Details isPopupDialog={true} template={template}></Details>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              className={classes.nextButton}
              onClick={template.goNext}
              disabled={isLoading}
              color="primary"
              variant="contained">
              Next
            </Button>
          </DialogActions>
        </Dialog>
      ) : (
        <Dialog
          open={showTemplateCreateModal}
          aria-labelledby="form-dialog-title"
          fullWidth
          maxWidth="lg">
          <Typography variant="h3">
            <DialogTitle
              style={{ marginTop: '8px' }}
              id="form-dialog-title"
              disableTypography={true}>
              <div>Survey Result Response Template Design</div>
              <div className={classes.dialogSubTitle}>
                * indicates a required field
              </div>
            </DialogTitle>
            <IconButton
              aria-label="close"
              className={classes.closeButton}
              onClick={() => {
                closeCreateTemplateDialog();
                setIsCustomizeTemplate(false);
              }}>
              <CloseIcon />
            </IconButton>
          </Typography>
          <DialogContent>
            {
              <Design
                isPopupDialog={true}
                template={template}
                editor={editor}
                isCustomizeTemplate={isCustomizeTemplate}
                sendResponse={() => {
                  survey.onClickSendResponse(true);
                }}></Design>
            }
          </DialogContent>
          <DialogActions>
            {!isCustomizeTemplate && (
              <Button
                className={classes.backButton}
                onClick={template.goBack}
                disabled={isLoading}
                color="secondary"
                variant="contained">
                Back
              </Button>
            )}
            <Box flexGrow={1}>&nbsp;</Box>
            {isCustomizeTemplate && (
              <Button
                className={classes.sendButton}
                onClick={() => {
                  editor.current.exportAndSaveTemplate({
                    type: 'customizeTemplate'
                  });
                }}
                disabled={isLoading}
                color="primary"
                variant="contained">
                Send Response
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}
    </Dialog>
  );
};

export default SurveyResultResponseModal;
